import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock, Languages, Mail, MessageCircle, Video } from "lucide-react";
import { resolveImg } from "@/lib/media";
import { fade } from "./data";
import { emailHref, SocialLinks } from "./SocialLinks";

export const Announcement = ({ content }) => (
  <motion.section
    data-testid="announcement"
    variants={fade} initial="hidden" animate="show"
    className="mt-10 rounded-3xl border border-accent/25 bg-white/70 backdrop-blur shadow-[0_8px_40px_rgba(26,46,38,0.08)] overflow-hidden"
  >
    {content?.ann_banner_visible !== false && (
    <div
      data-testid="announcement-banner"
      className="relative px-7 py-8 flex items-center gap-3 border-b border-accent/20 bg-cover bg-center"
      style={content?.ann_image ? { backgroundImage: `url(${resolveImg(content.ann_image)})` } : undefined}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-[#1A2E26]/80 via-[#1A2E26]/55 to-[#1A2E26]/25" />
      <span className="relative grid place-items-center w-8 h-8 rounded-full bg-white/90 text-accent text-sm shrink-0">🙏</span>
      <p className="relative font-serif text-xl md:text-2xl leading-tight text-white drop-shadow">
        {content?.ann_banner || "Announcing New Morning Batch · 5:30am–8:30am from 1st Sep"}
      </p>
    </div>
    )}
    {content?.ann_notice_visible !== false && content?.ann_notice && (
      <div data-testid="announcement-notice" className="relative overflow-hidden border-b border-accent/20 bg-accent/10 py-3.5" aria-label={content.ann_notice}>
        <div className="ann-marquee">
          <span className="inline-block px-10 text-xl md:text-2xl font-bold text-accent tracking-tight">{content.ann_notice}</span>
          <span className="inline-block px-10 text-xl md:text-2xl font-bold text-accent tracking-tight" aria-hidden="true">{content.ann_notice}</span>
        </div>
      </div>
    )}
    <div className="px-7 py-7 grid md:grid-cols-2 gap-x-10 gap-y-7 text-sm leading-relaxed">
      <div className="space-y-5">
        <p className="text-base">{content?.training_line || "🙂 Positive Life @SSVS — 6 Days Training"}</p>
        {content?.ann_batch_visible !== false && (
        <div data-testid="announcement-batch-block" className="rounded-2xl border border-border bg-background/60 p-5">
          <p className="flex items-center gap-2 font-medium"><span className="grid place-items-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs">1</span> New Batch</p>
          <p className="mt-2 text-muted-foreground flex items-center gap-2"><Calendar size={14} className="text-accent" /> {content?.batch_dates || "1 Sep (Tue) – 6 Sep (Sun)"}</p>
          <p className="text-muted-foreground flex items-center gap-2"><Clock size={14} className="text-accent" /> {content?.batch_time || "5:30am – 8:30am IST"}</p>
        </div>
        )}
        {content?.ann_sharings_visible !== false && (
        <div data-testid="announcement-sharings-block" className="rounded-2xl border border-border bg-background/60 p-5">
          <p className="flex items-center gap-2 font-medium"><span className="grid place-items-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs">2</span> Experience Sharings</p>
          <p className="mt-2 text-muted-foreground">{content?.exp_when || "Sunday Evening, 6th Sep · 8:00pm–8:30pm"}</p>
          <p className="text-muted-foreground">All participants are welcome to share their experience of the program.</p>
        </div>
        )}
        {content?.ann_details_visible !== false && (
        <div data-testid="announcement-details-block" className="rounded-2xl border border-border bg-background/60 p-5">
          <p className="flex items-center gap-2 font-medium"><span className="grid place-items-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs">3</span> Program Details</p>
          <div className="mt-2 flex flex-col gap-2 text-muted-foreground">
            <span className="flex items-center gap-2"><Languages size={14} className="text-accent" /> Language: {content?.language_line || "Telugu"}</span>
            <span className="flex items-center gap-2"><Video size={14} className="text-accent" /> Mode: {content?.mode_line || "Pre-recorded videos"}</span>
            <span className="flex items-center gap-2"><MessageCircle size={14} className="text-accent" /> {content?.contact_numbers || "9000571911 / 9000571912"} (WhatsApp only)</span>
            {content?.whatsapp_form_visible !== false && (
              <Link to="/whatsapp" data-testid="whatsapp-updates-link" className="flex items-center gap-2 text-[#128C7E] hover:text-[#0f7568] transition-colors font-medium">
                <MessageCircle size={14} /> Get updates on WhatsApp <ArrowRight size={12} />
              </Link>
            )}
            {content?.social_email_visible !== false && emailHref(content?.social_email) && (
              <a href={emailHref(content.social_email)} data-testid="social-email" className="flex items-center gap-2 hover:text-accent transition-colors">
                <Mail size={14} className="text-accent" /> Email: <span className="text-foreground underline decoration-accent/40">{content.social_email.trim()}</span>
              </a>
            )}
          </div>
        </div>
        )}
      </div>

      <div className="space-y-5">
        {content?.ann_zoom_visible !== false && (
        <div data-testid="announcement-zoom-block" className="rounded-2xl border border-border bg-background/60 p-5">
          <div className="flex items-center gap-2 text-accent mb-2"><Video size={16} /><span className="text-xs uppercase tracking-widest font-medium">Common Zoom Link (New)</span></div>
          <a
            href={content?.zoom_link || "https://us02web.zoom.us/j/4252208747?pwd=dAGWG6k8X6bFIT54kEV9psRj6iD9lM.1"}
            target="_blank" rel="noopener noreferrer"
            data-testid="zoom-link"
            className="text-foreground underline decoration-accent/40 hover:text-accent break-all transition-colors"
          >
            Join Zoom Meeting
          </a>
          <p className="mt-2 text-muted-foreground">Meeting ID: <span className="text-foreground">{content?.zoom_meeting_id || "425 220 8747"}</span></p>
          <p className="text-muted-foreground">Passcode: <span className="text-foreground">{content?.zoom_passcode || "2020"}</span></p>
        </div>
        )}
        {content?.social_visible !== false && <SocialLinks content={content} />}
      </div>
    </div>
  </motion.section>
);
